import React, { useEffect, useMemo, useState } from 'react';
import { RotateCcw, Search, SlidersHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProperties } from '../../context/CMSContext';
import { PropertyCard } from '../../components/common/PropertyCard';
import {
  districts,
  categories,
  listingTypes,
  salePriceOptions,
  rentPriceOptions,
  filterProperties,
  type PropertyFilterState,
  defaultPropertyFilters,
} from '../../utils/propertyFilters';
import type { District, PropertyCategory, ListingType } from '../../types/cms';

const FilterField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
    {children}
  </div>
);

const PropertyGrid: React.FC = () => {
  const properties = useProperties();
  const [draftFilters, setDraftFilters] = useState<PropertyFilterState>(defaultPropertyFilters);
  const [activeFilters, setActiveFilters] = useState<PropertyFilterState>(defaultPropertyFilters);

  const filteredProperties = useMemo(() => filterProperties(properties, activeFilters), [properties, activeFilters]);

  // 分頁（2026-08-27 用戶指示）：每頁 20 個盤；篩選變動自動回第 1 頁
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters]);
  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / ITEMS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pagedProperties = useMemo(
    () => filteredProperties.slice((safeCurrentPage - 1) * ITEMS_PER_PAGE, safeCurrentPage * ITEMS_PER_PAGE),
    [filteredProperties, safeCurrentPage]
  );
  const goToPage = (p: number) => {
    setCurrentPage(p);
    document.getElementById('property-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // 頁碼視窗（最多顯示 7 個，聚焦當前頁）
  const pageNumbers = useMemo(() => {
    const all = Array.from({ length: totalPages }, (_, i) => i + 1);
    if (totalPages <= 7) return all;
    if (safeCurrentPage <= 4) return [...all.slice(0, 5), '…', totalPages];
    if (safeCurrentPage >= totalPages - 3) return [1, '…', ...all.slice(totalPages - 5)];
    return [1, '…', safeCurrentPage - 1, safeCurrentPage, safeCurrentPage + 1, '…', totalPages];
  }, [totalPages, safeCurrentPage]);

  const updateFilter = <K extends keyof PropertyFilterState>(key: K, value: PropertyFilterState[K]) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const resetFilters = () => {
    setDraftFilters(defaultPropertyFilters);
    setActiveFilters(defaultPropertyFilters);
  };

  const applyFilters = () => {
    setActiveFilters(draftFilters);
    setTimeout(() => {
      document.getElementById('property-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleKeywordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') applyFilters();
  };

  const priceOptions = draftFilters.listingType === 'rent' ? rentPriceOptions : salePriceOptions;

  return (
    <section id="properties" className="bg-[#f5f3ee] py-10 md:py-14">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <span className="mb-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-gold-700">
            <SlidersHorizontal className="h-4 w-4" />
            智能物業搜尋
          </span>
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">找到最適合您的物業</h2>
          <p className="text-slate-600">按買賣類型、地區、用途、面積及預算快速篩選，省卻逐個查找的時間。</p>
        </div>

        <div className="mx-auto mb-14 max-w-7xl rounded-xl border border-slate-200 bg-white p-5 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.45)] md:p-8">
          <div className="mb-5 flex items-center gap-2 text-sm font-bold text-slate-700">
            <SlidersHorizontal className="h-4 w-4 text-gold-700" />
            篩選條件
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <FilterField label="租售類型">
              <select
                value={draftFilters.listingType}
                onChange={(e) => updateFilter('listingType', e.target.value as ListingType | 'all')}
                className="w-full rounded border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-blue-800"
              >
                {listingTypes.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </FilterField>

            <FilterField label="地區">
              <select
                value={draftFilters.district}
                onChange={(e) => updateFilter('district', e.target.value as District | 'all')}
                className="w-full rounded border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-blue-800"
              >
                {districts.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </FilterField>

            <FilterField label="物業類型">
              <select
                value={draftFilters.category}
                onChange={(e) => updateFilter('category', e.target.value as PropertyCategory | 'all')}
                className="w-full rounded border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-blue-800"
              >
                {categories.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </FilterField>

            <FilterField label="售價範圍">
              <select
                value={draftFilters.salePriceRange}
                onChange={(e) => updateFilter('salePriceRange', e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-blue-800"
              >
                {salePriceOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </FilterField>

            <FilterField label="租金範圍">
              <select
                value={draftFilters.rentPriceRange}
                onChange={(e) => updateFilter('rentPriceRange', e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-blue-800"
              >
                {rentPriceOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </FilterField>

            <FilterField label="關鍵字">
              <input
                type="search"
                value={draftFilters.keyword}
                onChange={(e) => updateFilter('keyword', e.target.value)}
                onKeyDown={handleKeywordKeyDown}
                placeholder="屋苑／街道／樓盤編號"
                className="w-full rounded border border-slate-300 px-3 py-2.5 text-sm text-slate-800 outline-none placeholder:text-slate-500 focus:border-blue-800"
              />
            </FilterField>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-2 rounded border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50"
            >
              <RotateCcw className="h-4 w-4" />
              重設
            </button>
            <button
              type="button"
              onClick={applyFilters}
              className="flex items-center gap-2 rounded bg-blue-800 px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-900"
            >
              <Search className="h-4 w-4" />
              馬上搵盤
            </button>
          </div>
        </div>

        <div id="property-results" className="scroll-mt-24">
          <div className="mb-7 flex flex-wrap items-end justify-between gap-4 border-b border-slate-300 pb-5">
            <div>
              <p className="mb-1 text-sm font-semibold text-gold-700">搜尋結果</p>
              <h3 className="text-2xl font-bold text-slate-900">
                為您找到 <span className="text-blue-800">{filteredProperties.length}</span> 個合適物業
              </h3>
              {totalPages > 1 && (
                <p className="mt-1 text-xs text-slate-400">
                  顯示第 {(safeCurrentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(safeCurrentPage * ITEMS_PER_PAGE, filteredProperties.length)} 個（每頁 {ITEMS_PER_PAGE} 個）
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="flex min-h-touch items-center gap-2 px-3 text-sm font-semibold text-slate-600 transition-colors hover:text-blue-800"
            >
              <RotateCcw className="h-4 w-4" />
              重設所有篩選
            </button>
          </div>

          <motion.div layout className="grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {pagedProperties.map((property) => (
                <PropertyCard key={property.id} property={property} layout="grid" />
              ))}
            </AnimatePresence>
          </motion.div>

          {/* 分頁導航 */}
          {totalPages > 1 && (
            <nav className="mt-10 flex flex-wrap items-center justify-center gap-2" aria-label="分頁">
              <button
                type="button"
                onClick={() => goToPage(safeCurrentPage - 1)}
                disabled={safeCurrentPage === 1}
                className="flex h-10 items-center gap-1 rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-600 transition hover:border-blue-800 hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" /> 上一頁
              </button>
              {pageNumbers.map((p, i) =>
                typeof p === 'number' ? (
                  <button
                    key={p}
                    type="button"
                    onClick={() => goToPage(p)}
                    aria-current={p === safeCurrentPage ? 'page' : undefined}
                    className={`h-10 w-10 rounded-lg text-sm font-bold transition ${
                      p === safeCurrentPage
                        ? 'bg-blue-800 text-white'
                        : 'border border-slate-300 text-slate-600 hover:border-blue-800 hover:text-blue-800'
                    }`}
                  >
                    {p}
                  </button>
                ) : (
                  <span key={`ellipsis-${i}`} className="px-1 text-sm text-slate-400">…</span>
                )
              )}
              <button
                type="button"
                onClick={() => goToPage(safeCurrentPage + 1)}
                disabled={safeCurrentPage === totalPages}
                className="flex h-10 items-center gap-1 rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-600 transition hover:border-blue-800 hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                下一頁 <ChevronRight className="h-4 w-4" />
              </button>
            </nav>
          )}

          {filteredProperties.length === 0 && (
            <div className="border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
              <Search className="mx-auto mb-4 h-10 w-10 text-slate-300" />
              <h4 className="mb-2 text-xl font-bold text-slate-800">暫時未有完全符合的物業</h4>
              <p className="mb-5 text-sm text-slate-500">試試放寬地區、用途或價錢條件。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="min-h-touch bg-blue-800 px-6 py-3 text-sm font-bold text-white hover:bg-blue-900"
              >
                查看所有物業
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PropertyGrid;
